import {
  ClipboardCopy,
  Download,
  Folder,
  FolderOpen,
  Grid3X3,
  HardDrive,
  HelpCircle,
  ImagePlus,
  RefreshCw,
  ScanLine,
  Settings,
  ShieldCheck,
  Upload,
  WifiOff,
} from "lucide-react";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BoardManagementDialog,
  type BoardManagementMode,
  type BoardManagementRequest,
} from "../components/board_management_dialog";
import { BoardRecoveryScreen } from "../components/board_recovery_screen";
import { type BoardConflictKind, ConflictDialog } from "../components/conflict_dialog";
import { CreateFolderDialog } from "../components/create_folder_dialog";
import { ExportDialog } from "../components/export_dialog";
import { FolderMoveDialog } from "../components/folder_move_dialog";
import { HelpDialog } from "../components/help_dialog";
import { OperationRecoveryScreen } from "../components/operation_recovery_screen";
import { RecoveryCopyDialog } from "../components/recovery_copy_dialog";
import { SaveFailureDialog } from "../components/save_failure_dialog";
import { SaveStatusView } from "../components/save_status";
import { TrashDialog } from "../components/trash_dialog";
import { WorkspaceNavigator } from "../components/workspace_navigator";
import { resolveBoardNameCollision } from "../domain/board_naming";
import { formatStoredPath, parseStoredPath } from "../domain/paths";
import type { EditorCommands, EditorUiState } from "../editor/editor_commands";
import type { EditorSnapshot } from "../editor/editor_snapshot";
import type { EditorSnapshotOrigin } from "../editor/excalidraw_adapter";
import { downloadBlob } from "../export/download_blob";
import { AutosaveController, type SaveStatus } from "../persistence/autosave_controller";
import {
  BoardConflictError,
  BoardOpenError,
  BoardSession,
  type PreviewProducer,
  type RecoveryPort,
} from "../persistence/board_session";
import type { RecentBoardRecord, RecentWorkspaceRecord } from "../persistence/local_database";
import { RecoveryStore } from "../recovery/recovery_store";
import {
  type BoardLease,
  BoardLeaseCoordinator,
  type BoardLeaseMode,
  type BoardMigrationLease,
  BrowserBoardMigrationLease,
} from "../workspace/board_lease";
import {
  BRAVE_FILE_ACCESS_SETTING_URL,
  type BrowserCapabilityRecoveryResult,
  detectUnsupportedBrowser,
  openBraveFileAccessSetting,
  type UnsupportedBrowserKind,
} from "../workspace/browser_capability_recovery";
import {
  type DirectoryOperationProgress,
  type DirectoryOperationRequest,
  DirectoryOperationService,
  type PendingDirectoryOperation,
  TRASH_PATH,
} from "../workspace/directory_operation";
import { OpfsWorkspaceConnector } from "../workspace/opfs_workspace_connector";
import { RecentBoardStore } from "../workspace/recent_board_store";
import { RecentWorkspaceStore } from "../workspace/recent_workspace_store";
import {
  BrowserWorkspaceConnector,
  type ConnectedWorkspace,
  type WorkspaceConnector,
  type WorkspacePickerIntent,
} from "../workspace/workspace_connector";
import { type WorkspaceManifest, WorkspaceManifestStore } from "../workspace/workspace_manifest";
import {
  planWorkspaceMove,
  remapWorkspacePath,
  type WorkspaceMoveItem,
} from "../workspace/workspace_move";
import {
  type ScannedBoard,
  type ScannedFolder,
  scanTrash,
  scanWorkspace,
  type TrashedBoard,
  type WorkspaceScan,
} from "../workspace/workspace_scan";
import { useTheme } from "./theme_provider";
import type { RegisterUpdateFlush } from "./update_coordinator";

const LAST_BOARD_KEY = "shotboard.last_board";

/** Supplies current time without recreating callback identity during React renders. */
const defaultNow = () => new Date();

/** Creates the production cross-tab lease behind the injectable application boundary. */
const defaultCreateBoardLease = (key: string, onMode: (mode: BoardLeaseMode) => void): BoardLease =>
  new BoardLeaseCoordinator(key, onMode);
const defaultMigrationLease = new BrowserBoardMigrationLease();

/** Returns the validated identity required for one workspace-scoped writer lock. */
function requireBoardId(board: ScannedBoard): string {
  if (board.id === null) {
    throw new Error(`Board ${formatStoredPath(board.path)} has no validated identity.`);
  }
  return board.id;
}

const ExcalidrawAdapter = lazy(async () => {
  const module = await import("../editor/excalidraw_adapter");
  return { default: module.ExcalidrawAdapter };
});

interface RecentWorkspacePort {
  mostRecent(): Promise<RecentWorkspaceRecord | null>;
  remember(handle: FileSystemDirectoryHandle, openedAt: Date): Promise<RecentWorkspaceRecord>;
}

interface RecentBoardPort {
  list(workspaceId: string, limit?: number): Promise<readonly RecentBoardRecord[]>;
  remapPathPrefix(
    workspaceId: string,
    sourcePath: readonly string[],
    destinationPath: readonly string[] | null,
  ): Promise<void>;
  remember(
    workspaceId: string,
    path: readonly string[],
    openedAt: Date,
  ): Promise<RecentBoardRecord>;
}

export interface WorkspaceApplicationDependencies {
  readonly connector: WorkspaceConnector;
  readonly createBoardLease?: (key: string, onMode: (mode: BoardLeaseMode) => void) => BoardLease;
  readonly migrationLease?: BoardMigrationLease;
  readonly recentBoards: RecentBoardPort;
  readonly recentWorkspaces: RecentWorkspacePort;
  readonly recovery: RecoveryPort;
  readonly detectUnsupportedBrowser?: () => Promise<UnsupportedBrowserKind>;
  readonly recoverBrowserCapability?: () => Promise<BrowserCapabilityRecoveryResult>;
  readonly now?: () => Date;
}

type ApplicationState =
  | { readonly kind: "checking" }
  | { readonly kind: "welcome"; readonly error?: string }
  | { readonly kind: "reconnect"; readonly recent: RecentWorkspaceRecord; readonly error?: string }
  | {
      readonly kind: "workspace";
      readonly connection: ConnectedWorkspace;
      readonly manifest: WorkspaceManifest;
      readonly recentBoardPaths: readonly string[];
      readonly scan: WorkspaceScan;
      readonly session: BoardSession;
      readonly trash: readonly TrashedBoard[];
      readonly workspaceId: string;
    }
  | {
      readonly kind: "board_recovery";
      readonly boardPath: readonly string[];
      readonly connection: ConnectedWorkspace;
      readonly issue: string;
      readonly recoveryKind: BoardOpenError["kind"];
      readonly raw: string;
      readonly workspaceId: string;
    }
  | {
      readonly kind: "operation_recovery";
      readonly connection: ConnectedWorkspace;
      readonly pending: PendingDirectoryOperation;
      readonly workspaceId: string;
    }
  | { readonly kind: "error"; readonly message: string };

type WorkspaceRefreshResult = "ok" | BoardConflictKind | "permission_lost";

/** Creates production services while compiling OPFS behavior only into test-mode branches. */
function createDefaultDependencies(): WorkspaceApplicationDependencies {
  const connector =
    import.meta.env.MODE === "test"
      ? new OpfsWorkspaceConnector()
      : new BrowserWorkspaceConnector();
  return {
    connector,
    recentBoards: new RecentBoardStore(),
    recentWorkspaces: new RecentWorkspaceStore(),
    recovery: new RecoveryStore(),
  };
}

const defaultDependencies = createDefaultDependencies();

/** Owns first-run, reconnect, workspace, and active-board composition. */
export function WorkspaceApplication({
  dependencies = defaultDependencies,
  registerUpdateFlush,
}: {
  readonly dependencies?: WorkspaceApplicationDependencies;
  readonly registerUpdateFlush?: RegisterUpdateFlush;
}) {
  const [state, setState] = useState<ApplicationState>({ kind: "checking" });
  const [busy, setBusy] = useState(false);
  const [operationProgress, setOperationProgress] = useState<DirectoryOperationProgress | null>(
    null,
  );
  const operationAbortRef = useRef<AbortController | null>(null);
  const { theme, setTheme } = useTheme();
  const now = dependencies.now ?? defaultNow;
  const migrationLease = dependencies.migrationLease ?? defaultMigrationLease;

  /** Loads the complete derived navigator state and one safe active editor session. */
  const loadWorkspace = useCallback(
    async (
      connection: ConnectedWorkspace,
      workspaceId: string,
      preferredPath: readonly string[] | null = readLastBoardPath(),
    ): Promise<void> => {
      const [scan, manifest, recentRecords, trash] = await Promise.all([
        scanWorkspace(connection.gateway),
        new WorkspaceManifestStore(connection.gateway, now).load(),
        dependencies.recentBoards.list(workspaceId),
        scanTrash(connection.gateway),
      ]);
      const matchingBoard =
        preferredPath === null
          ? undefined
          : scan.boards.find(
              (board) => formatStoredPath(board.path) === formatStoredPath(preferredPath),
            );
      if (
        matchingBoard !== undefined &&
        (matchingBoard.status === "invalid" || matchingBoard.status === "newer")
      ) {
        const raw = new TextDecoder().decode(
          await connection.gateway.readFile([...matchingBoard.path, "board.json"]),
        );
        setState({
          kind: "board_recovery",
          boardPath: matchingBoard.path,
          connection,
          issue: matchingBoard.issue ?? "This board cannot be opened safely.",
          recoveryKind: matchingBoard.status,
          raw,
          workspaceId,
        });
        return;
      }
      let session: BoardSession;
      try {
        session =
          matchingBoard === undefined
            ? BoardSession.createDraft(connection.gateway, dependencies.recovery, { now })
            : await BoardSession.open(
                connection.gateway,
                dependencies.recovery,
                matchingBoard.path,
                {
                  now,
                  migrationLease,
                  migrationLeaseKey: `${workspaceId}:${requireBoardId(matchingBoard)}`,
                },
              );
      } catch (error) {
        if (error instanceof BoardOpenError && matchingBoard !== undefined) {
          setState({
            kind: "board_recovery",
            boardPath: matchingBoard.path,
            connection,
            issue: error.message,
            recoveryKind: error.kind,
            raw: error.raw,
            workspaceId,
          });
          return;
        }
        throw error;
      }
      if (matchingBoard === undefined) {
        window.localStorage.removeItem(LAST_BOARD_KEY);
      } else {
        window.localStorage.setItem(LAST_BOARD_KEY, formatStoredPath(matchingBoard.path));
      }
      setState({
        kind: "workspace",
        connection,
        manifest,
        recentBoardPaths: recentRecords.map((record) => record.boardPath),
        scan,
        session,
        trash,
        workspaceId,
      });
    },
    [dependencies.recentBoards, dependencies.recovery, migrationLease, now],
  );

  /** Stops at a pending journal before scanning normal workspace content. */
  const enterWorkspace = useCallback(
    async (connection: ConnectedWorkspace, workspaceId: string): Promise<void> => {
      const pending = await new DirectoryOperationService(connection.gateway).inspect();
      if (pending !== null) {
        setState({ kind: "operation_recovery", connection, pending, workspaceId });
        return;
      }
      await loadWorkspace(connection, workspaceId);
    },
    [loadWorkspace],
  );

  useEffect(() => {
    let active = true;

    /** Restores a recent handle without causing a permission prompt on load. */
    async function bootstrap(): Promise<void> {
      try {
        const recent = await dependencies.recentWorkspaces.mostRecent();
        if (!active) {
          return;
        }
        if (recent === null) {
          setState({ kind: "welcome" });
          return;
        }

        const connection = dependencies.connector.fromHandle(recent.handle);
        const permission = await connection.gateway.queryPermission();
        if (!active) {
          return;
        }
        if (permission === "granted") {
          await enterWorkspace(connection, recent.id);
        } else {
          setState({ kind: "reconnect", recent });
        }
      } catch (error) {
        if (active) {
          const detail = error instanceof Error ? ` ${error.message}` : "";
          setState({
            kind: "welcome",
            error: `Shotboard could not inspect the recent workspace. Choose it again to reconnect safely.${detail}`,
          });
        }
      }
    }

    void bootstrap();
    return () => {
      active = false;
    };
  }, [dependencies.connector, dependencies.recentWorkspaces, enterWorkspace]);

  /** Handles the user-gesture picker boundary and remembers only successful choices. */
  const chooseWorkspace = useCallback(
    async (intent: WorkspacePickerIntent): Promise<void> => {
      setBusy(true);
      try {
        const connection = await dependencies.connector.choose(intent);
        const recent = await dependencies.recentWorkspaces.remember(connection.handle, now());
        await enterWorkspace(connection, recent.id);
      } catch (error) {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setState({
            kind: "welcome",
            error:
              "Workspace access failed. No files were changed. Choose a writable folder and try again.",
          });
        }
      } finally {
        setBusy(false);
      }
    },
    [dependencies.connector, dependencies.recentWorkspaces, enterWorkspace, now],
  );

  /** Re-requests recent-handle permission only from an explicit activation. */
  const reconnect = useCallback(async (): Promise<void> => {
    if (state.kind !== "reconnect") {
      return;
    }
    setBusy(true);
    const connection = dependencies.connector.fromHandle(state.recent.handle);
    try {
      const permission = await connection.gateway.requestPermission().catch(() => null);
      if (permission === null) {
        setState({
          ...state,
          error: permissionRequestError(),
        });
        return;
      }
      if (permission !== "granted") {
        setState({
          ...state,
          error: "Permission remains unavailable. The workspace files are unchanged.",
        });
        return;
      }
      const recent = await dependencies.recentWorkspaces.remember(connection.handle, now());
      await enterWorkspace(connection, recent.id);
    } finally {
      setBusy(false);
    }
  }, [dependencies.connector, dependencies.recentWorkspaces, enterWorkspace, now, state]);

  /** Refreshes derived state while preserving in-memory work across external divergence. */
  const refreshWorkspace = useCallback(
    async (
      connection: ConnectedWorkspace,
      workspaceId: string,
      session: BoardSession,
    ): Promise<WorkspaceRefreshResult> => {
      try {
        if ((await connection.gateway.queryPermission()) !== "granted") {
          return "permission_lost";
        }
        if (session.boardPath !== null) {
          await dependencies.recentBoards.remember(workspaceId, session.boardPath, now());
        }
        const [scan, manifest, recentRecords, trash] = await Promise.all([
          scanWorkspace(connection.gateway),
          new WorkspaceManifestStore(connection.gateway, now).load(),
          dependencies.recentBoards.list(workspaceId),
          scanTrash(connection.gateway),
        ]);
        let nextSession = session;
        let result: WorkspaceRefreshResult = "ok";
        const currentPath = session.boardPath;
        if (currentPath !== null) {
          const currentBoard = scan.boards.find(
            (board) => formatStoredPath(board.path) === formatStoredPath(currentPath),
          );
          if (currentBoard !== undefined) {
            if (await session.hasDiskConflict()) {
              result = "changed";
            } else {
              window.localStorage.setItem(LAST_BOARD_KEY, formatStoredPath(currentPath));
            }
          } else {
            const identityMatches = scan.boards.filter(
              (board) =>
                (board.status === "valid" || board.status === "migration") &&
                board.id === session.boardId,
            );
            if (identityMatches.length === 1 && identityMatches[0] !== undefined) {
              nextSession = await BoardSession.open(
                connection.gateway,
                dependencies.recovery,
                identityMatches[0].path,
                {
                  now,
                  migrationLease,
                  migrationLeaseKey: `${workspaceId}:${requireBoardId(identityMatches[0])}`,
                },
              );
              window.localStorage.setItem(
                LAST_BOARD_KEY,
                formatStoredPath(identityMatches[0].path),
              );
            } else {
              result = identityMatches.length > 1 ? "ambiguous" : "removed";
            }
          }
        }
        setState((currentState) => {
          if (
            currentState.kind !== "workspace" ||
            currentState.connection !== connection ||
            currentState.workspaceId !== workspaceId
          ) {
            return currentState;
          }
          return {
            ...currentState,
            manifest,
            recentBoardPaths: recentRecords.map((record) => record.boardPath),
            scan,
            session: nextSession,
            trash,
          };
        });
        return result;
      } catch (error) {
        if (isPermissionError(error)) {
          return "permission_lost";
        }
        throw error;
      }
    },
    [dependencies.recentBoards, dependencies.recovery, migrationLease, now],
  );

  /** Opens a validated board and scopes a fresh editor session to it. */
  const openBoard = useCallback(
    async (board: ScannedBoard): Promise<void> => {
      if (state.kind !== "workspace") {
        return;
      }
      try {
        if (board.status === "invalid" || board.status === "newer") {
          const raw = new TextDecoder().decode(
            await state.connection.gateway.readFile([...board.path, "board.json"]),
          );
          setState({
            kind: "board_recovery",
            boardPath: board.path,
            connection: state.connection,
            issue: board.issue ?? "This board requires non-destructive recovery.",
            recoveryKind: board.status,
            raw,
            workspaceId: state.workspaceId,
          });
          window.localStorage.setItem(LAST_BOARD_KEY, formatStoredPath(board.path));
          return;
        }
        const session = await BoardSession.open(
          state.connection.gateway,
          dependencies.recovery,
          board.path,
          {
            now,
            migrationLease,
            migrationLeaseKey: `${state.workspaceId}:${requireBoardId(board)}`,
          },
        );
        window.localStorage.setItem(LAST_BOARD_KEY, formatStoredPath(board.path));
        await dependencies.recentBoards.remember(state.workspaceId, board.path, now());
        const recentRecords = await dependencies.recentBoards.list(state.workspaceId);
        setState({
          ...state,
          recentBoardPaths: recentRecords.map((record) => record.boardPath),
          session,
        });
      } catch (error) {
        if (error instanceof BoardOpenError) {
          setState({
            kind: "board_recovery",
            boardPath: board.path,
            connection: state.connection,
            issue: error.message,
            recoveryKind: error.kind,
            raw: error.raw,
            workspaceId: state.workspaceId,
          });
          window.localStorage.setItem(LAST_BOARD_KEY, formatStoredPath(board.path));
          return;
        }
        throw error;
      }
    },
    [dependencies.recentBoards, dependencies.recovery, migrationLease, now, state],
  );

  /** Starts a blank draft without creating canonical files until the first mutation. */
  const newBoard = useCallback((): void => {
    if (state.kind !== "workspace") {
      return;
    }
    window.localStorage.removeItem(LAST_BOARD_KEY);
    setState({
      ...state,
      session: BoardSession.createDraft(state.connection.gateway, dependencies.recovery, { now }),
    });
  }, [dependencies.recovery, now, state]);

  /** Runs one journaled operation and follows the active board to its safe result. */
  const runDirectoryOperation = useCallback(
    async (request: DirectoryOperationRequest): Promise<void> => {
      if (state.kind !== "workspace") {
        return;
      }
      const currentPath = state.session.boardPath;
      const movedCurrentPath =
        currentPath === null
          ? null
          : remapWorkspacePath(currentPath, request.sourcePath, request.destinationPath);
      const service = new DirectoryOperationService(state.connection.gateway, {
        now,
        onProgress: setOperationProgress,
      });
      const abortController = new AbortController();
      operationAbortRef.current = abortController;
      try {
        await service.start(request, abortController.signal);
        if (request.type !== "duplicate" && request.type !== "restore") {
          await new WorkspaceManifestStore(state.connection.gateway, now).remapPathPrefix(
            request.sourcePath,
            request.type === "trash" ? null : request.destinationPath,
          );
        }
        if (request.type !== "duplicate" && request.type !== "restore") {
          await dependencies.recentBoards.remapPathPrefix(
            state.workspaceId,
            request.sourcePath,
            request.type === "trash" ? null : request.destinationPath,
          );
        }
        const preferredPath =
          request.type === "trash" && movedCurrentPath !== null
            ? null
            : request.type === "duplicate"
              ? currentPath
              : (movedCurrentPath ?? currentPath);
        await loadWorkspace(state.connection, state.workspaceId, preferredPath);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          const pending = await service.inspect();
          if (pending !== null) {
            setState({
              kind: "operation_recovery",
              connection: state.connection,
              pending,
              workspaceId: state.workspaceId,
            });
          }
          return;
        }
        throw error;
      } finally {
        operationAbortRef.current = null;
        setOperationProgress(null);
      }
    },
    [dependencies.recentBoards, loadWorkspace, now, state],
  );

  /** Maps one management form result into a collision-safe journal request. */
  const manageBoard = useCallback(
    async (board: ScannedBoard, request: BoardManagementRequest): Promise<void> => {
      if (state.kind !== "workspace") {
        return;
      }
      const parent = board.path.slice(0, -1);
      let operation: DirectoryOperationRequest;
      if (request.type === "rename" || request.type === "duplicate") {
        operation = {
          type: request.type,
          sourcePath: board.path,
          destinationPath: [...parent, request.name],
        };
      } else if (request.type === "move") {
        operation = {
          type: "move",
          sourcePath: board.path,
          destinationPath: [...request.folderPath, board.name],
        };
      } else {
        const trashName = resolveBoardNameCollision(
          board.name,
          new Set(state.trash.map((entry) => entry.name)),
        );
        operation = {
          type: "trash",
          sourcePath: board.path,
          destinationPath: [...TRASH_PATH, trashName],
        };
      }
      await runDirectoryOperation(operation);
    },
    [runDirectoryOperation, state],
  );

  /** Maps navigator drag or keyboard folder movement into the durable operation service. */
  const moveWorkspaceItem = useCallback(
    async (item: WorkspaceMoveItem, destinationFolderPath: readonly string[]): Promise<void> => {
      const plan = planWorkspaceMove(item, destinationFolderPath);
      await runDirectoryOperation({
        type: "move",
        sourcePath: plan.sourcePath,
        destinationPath: plan.destinationPath,
      });
    },
    [runDirectoryOperation],
  );

  /** Creates one root organizational folder after an explicit collision check. */
  const createFolder = useCallback(
    async (name: string): Promise<void> => {
      if (state.kind !== "workspace") {
        return;
      }
      if (await state.connection.gateway.fileExists([name])) {
        throw new Error("A workspace entry with that name already exists.");
      }
      await state.connection.gateway.createDirectory([name]);
      await refreshWorkspace(state.connection, state.workspaceId, state.session);
    },
    [refreshWorkspace, state],
  );

  /** Toggles one portable pin without altering the canonical board directory. */
  const togglePin = useCallback(
    async (board: ScannedBoard): Promise<void> => {
      if (state.kind !== "workspace") {
        return;
      }
      const manifest = await new WorkspaceManifestStore(state.connection.gateway, now).togglePin(
        board.path,
      );
      setState({ ...state, manifest });
    },
    [now, state],
  );

  /** Restores one trash entry to its original parent with an explicit collision suffix. */
  const restoreBoard = useCallback(
    async (board: TrashedBoard): Promise<void> => {
      if (state.kind !== "workspace") {
        return;
      }
      const original = board.trashMetadata.originalPath;
      const parent = original.slice(0, -1);
      await ensureFolderPath(state.connection, parent);
      const originalName = original.at(-1);
      if (originalName === undefined) {
        throw new Error("The trash entry has no original board name.");
      }
      const entries = await state.connection.gateway.listDirectory(parent);
      const restoredName = resolveBoardNameCollision(
        originalName,
        new Set(entries.map((entry) => entry.name)),
      );
      await runDirectoryOperation({
        type: "restore",
        sourcePath: board.path,
        destinationPath: [...parent, restoredName],
      });
    },
    [runDirectoryOperation, state],
  );

  /** Permanently clears reserved trash through the bounded gateway operation. */
  const emptyTrash = useCallback(async (): Promise<void> => {
    if (state.kind !== "workspace") {
      return;
    }
    await new DirectoryOperationService(state.connection.gateway).emptyTrash();
    await refreshWorkspace(state.connection, state.workspaceId, state.session);
  }, [refreshWorkspace, state]);

  /** Resolves an interrupted journal before entering normal workspace state. */
  const resolveOperationRecovery = useCallback(
    async (action: "resume" | "rollback"): Promise<void> => {
      if (state.kind !== "operation_recovery") {
        return;
      }
      const service = new DirectoryOperationService(state.connection.gateway, {
        onProgress: setOperationProgress,
      });
      try {
        if (action === "resume") {
          await service.resume();
          const lastPath = readLastBoardPath();
          if (
            lastPath !== null &&
            formatStoredPath(lastPath) === formatStoredPath(state.pending.operation.sourcePath)
          ) {
            window.localStorage.setItem(
              LAST_BOARD_KEY,
              formatStoredPath(state.pending.operation.destinationPath),
            );
          }
        } else {
          await service.rollback();
        }
        await loadWorkspace(state.connection, state.workspaceId);
      } finally {
        setOperationProgress(null);
      }
    },
    [loadWorkspace, state],
  );

  if (state.kind === "checking") {
    return (
      <main className="loading-screen" aria-busy="true">
        <span className="loading-screen__mark" aria-hidden="true">
          S
        </span>
        <p>Checking recent workspace…</p>
      </main>
    );
  }

  if (state.kind === "error") {
    return (
      <main className="fatal-error">
        <p className="eyebrow">Board kept read-only</p>
        <h1>Shotboard stopped before changing this board.</h1>
        <p>{state.message}</p>
        <button type="button" onClick={() => setState({ kind: "welcome" })}>
          Choose another workspace
        </button>
      </main>
    );
  }

  if (state.kind === "operation_recovery") {
    return (
      <OperationRecoveryScreen
        pending={state.pending}
        progress={operationProgress}
        workspaceName={state.connection.gateway.name}
        onResume={() => resolveOperationRecovery("resume")}
        onRollback={() => resolveOperationRecovery("rollback")}
        onSwitchWorkspace={() => setState({ kind: "welcome" })}
      />
    );
  }

  if (state.kind === "board_recovery") {
    return (
      <BoardRecoveryScreen
        boardPath={state.boardPath}
        issue={state.issue}
        kind={state.recoveryKind}
        workspaceName={state.connection.gateway.name}
        onBack={() => loadWorkspace(state.connection, state.workspaceId, null)}
        onDownload={() =>
          downloadBlob(
            new Blob([state.raw], { type: "application/json" }),
            `${state.boardPath.at(-1) ?? "shotboard"}-raw-board.json`,
          )
        }
        onRetry={() => loadWorkspace(state.connection, state.workspaceId, state.boardPath)}
        onSwitchWorkspace={() => setState({ kind: "welcome" })}
      />
    );
  }

  if (state.kind === "workspace") {
    return (
      <WorkspaceEditor
        createBoardLease={dependencies.createBoardLease ?? defaultCreateBoardLease}
        registerUpdateFlush={registerUpdateFlush}
        state={state}
        theme={theme === "system" ? resolvedDocumentTheme() : theme}
        onThemeChange={setTheme}
        onRefresh={refreshWorkspace}
        onLoadPath={(path) => loadWorkspace(state.connection, state.workspaceId, path)}
        onOpenBoard={openBoard}
        onNewBoard={newBoard}
        onCreateFolder={createFolder}
        onEmptyTrash={emptyTrash}
        onManageBoard={manageBoard}
        onMoveItem={moveWorkspaceItem}
        onRestoreBoard={restoreBoard}
        onTogglePin={togglePin}
        operationProgress={operationProgress}
        onCancelOperation={() => operationAbortRef.current?.abort()}
        onSwitchWorkspace={() => setState({ kind: "welcome" })}
      />
    );
  }

  return (
    <WelcomeScreen
      reconnectState={state.kind === "reconnect" ? state : null}
      supported={dependencies.connector.isSupported()}
      busy={busy}
      theme={theme}
      onThemeChange={setTheme}
      onChoose={chooseWorkspace}
      onReconnect={reconnect}
      onDetectUnsupportedBrowser={dependencies.detectUnsupportedBrowser ?? detectUnsupportedBrowser}
      onRecoverBrowserCapability={
        dependencies.recoverBrowserCapability ?? openBraveFileAccessSetting
      }
      error={state.kind === "welcome" ? state.error : undefined}
    />
  );
}

interface WelcomeScreenProps {
  readonly reconnectState: Extract<ApplicationState, { kind: "reconnect" }> | null;
  readonly supported: boolean;
  readonly busy: boolean;
  readonly theme: string;
  readonly error: string | undefined;
  readonly onThemeChange: (theme: string) => void;
  readonly onChoose: (intent: WorkspacePickerIntent) => Promise<void>;
  readonly onReconnect: () => Promise<void>;
  readonly onDetectUnsupportedBrowser: () => Promise<UnsupportedBrowserKind>;
  readonly onRecoverBrowserCapability: () => Promise<BrowserCapabilityRecoveryResult>;
}

/** Renders first-run privacy and direct permission actions. */
function WelcomeScreen({
  reconnectState,
  supported,
  busy,
  theme,
  error,
  onThemeChange,
  onChoose,
  onReconnect,
  onDetectUnsupportedBrowser,
  onRecoverBrowserCapability,
}: WelcomeScreenProps) {
  const visibleError = reconnectState?.error ?? error;
  const [unsupportedBrowser, setUnsupportedBrowser] = useState<UnsupportedBrowserKind>("other");
  const [capabilityRecoveryResult, setCapabilityRecoveryResult] = useState<
    BrowserCapabilityRecoveryResult | "opening" | null
  >(null);

  useEffect(() => {
    if (supported) {
      return;
    }

    let active = true;
    void onDetectUnsupportedBrowser()
      .then((browser) => {
        if (active) {
          setUnsupportedBrowser(browser);
        }
      })
      .catch(() => {
        if (active) {
          setUnsupportedBrowser("other");
        }
      });
    return () => {
      active = false;
    };
  }, [onDetectUnsupportedBrowser, supported]);

  const recoverBrowserCapability = async (): Promise<void> => {
    setCapabilityRecoveryResult("opening");
    setCapabilityRecoveryResult(await onRecoverBrowserCapability());
  };

  return (
    <main className="welcome">
      <Header theme={theme} onThemeChange={onThemeChange} />
      <section className="welcome__content" aria-labelledby="welcome-title">
        <p className="eyebrow">Local screenshot workspace</p>
        <h1 id="welcome-title">Mark the point. Keep the source.</h1>
        <p className="lede">
          Paste screenshots onto an infinite canvas, explain them with familiar drawing tools, and
          keep every editable board in a folder you control.
        </p>

        {reconnectState === null ? (
          <div className="welcome-actions">
            <button
              className="primary-action"
              type="button"
              disabled={!supported || busy}
              onClick={() => void onChoose("choose")}
            >
              <FolderOpen aria-hidden="true" />
              Choose Workspace
            </button>
            <button
              className="secondary-action"
              type="button"
              disabled={!supported || busy}
              onClick={() => void onChoose("create")}
            >
              <Folder aria-hidden="true" />
              Create Workspace
            </button>
          </div>
        ) : (
          <div className="reconnect-panel">
            <p>
              Reconnect <strong>{reconnectState.recent.name}</strong> to continue. Request File
              Access opens the browser’s read/write permission prompt.
            </p>
            <div className="welcome-actions">
              <button
                className="primary-action"
                type="button"
                disabled={busy}
                onClick={() => void onReconnect()}
              >
                <FolderOpen aria-hidden="true" />
                Request File Access
              </button>
              <button
                className="secondary-action"
                type="button"
                disabled={busy}
                onClick={() => void onChoose("choose")}
              >
                Choose Another Workspace
              </button>
            </div>
          </div>
        )}

        {!supported ? (
          <section className="capability-recovery" aria-labelledby="capability-recovery-title">
            <h2 id="capability-recovery-title">
              {unsupportedBrowser === "brave"
                ? "Enable local folder access in Brave"
                : "Local folder access is unavailable"}
            </h2>
            <p className="permission-error" role="alert">
              Shotboard requires a current desktop Chromium browser with local folder access.
            </p>
            {unsupportedBrowser === "brave" ? (
              <>
                <p>
                  Brave disables the required File System Access API by default. Open its setting,
                  enable <strong>File System Access API</strong>, relaunch Brave, then reload
                  Shotboard.
                </p>
                <code>{BRAVE_FILE_ACCESS_SETTING_URL}</code>
              </>
            ) : (
              <p>
                Open Shotboard in a current desktop Chrome, Edge, or another Chromium browser with
                the File System Access API enabled, then reload this page.
              </p>
            )}
            <div className="welcome-actions">
              {unsupportedBrowser === "brave" ? (
                <button
                  className="primary-action"
                  type="button"
                  disabled={capabilityRecoveryResult === "opening"}
                  onClick={() => void recoverBrowserCapability()}
                >
                  <Settings aria-hidden="true" />
                  {capabilityRecoveryResult === "opening"
                    ? "Opening Brave Setting…"
                    : "Open Brave File Access Setting"}
                </button>
              ) : null}
              <button
                className={unsupportedBrowser === "brave" ? "secondary-action" : "primary-action"}
                type="button"
                onClick={() => window.location.reload()}
              >
                <RefreshCw aria-hidden="true" />
                Reload Shotboard
              </button>
            </div>
            {unsupportedBrowser === "brave" && capabilityRecoveryResult === "copied" ? (
              <p className="capability-recovery__status" role="status">
                The setting address was copied. If Brave blocked the new tab, paste it into the
                address bar.
              </p>
            ) : null}
            {unsupportedBrowser === "brave" && capabilityRecoveryResult === "manual" ? (
              <p className="capability-recovery__status" role="status">
                Brave blocked the new tab and clipboard. Copy the setting address above into the
                address bar.
              </p>
            ) : null}
          </section>
        ) : null}
        {visibleError ? (
          <p className="permission-error" role="alert">
            {visibleError}
          </p>
        ) : null}

        <ul className="principles" aria-label="Shotboard privacy promises">
          <li>
            <HardDrive aria-hidden="true" />
            <span>
              <strong>Your folder is canonical.</strong>
              Boards remain ordinary local files.
            </span>
          </li>
          <li>
            <ShieldCheck aria-hidden="true" />
            <span>
              <strong>Originals stay intact.</strong>
              Source image bytes are preserved.
            </span>
          </li>
          <li>
            <WifiOff aria-hidden="true" />
            <span>
              <strong>No application API.</strong>
              Editing keeps working offline.
            </span>
          </li>
        </ul>
      </section>
      <footer className="privacy-line">
        <span aria-hidden="true" />
        Files remain on this device. Shotboard has no accounts, analytics, or telemetry.
      </footer>
    </main>
  );
}

interface HeaderProps {
  readonly theme: string;
  readonly onThemeChange: (theme: string) => void;
}

/** Renders the shared wordmark and local theme preference. */
function Header({ theme, onThemeChange }: HeaderProps) {
  return (
    <header className="topbar">
      <a className="wordmark" href="/" aria-label="Shotboard home">
        <span className="wordmark__mark" aria-hidden="true">
          S
        </span>
        Shotboard
      </a>
      <label className="theme-control">
        <span>Theme</span>
        <select
          value={theme}
          onChange={(event) => onThemeChange(event.currentTarget.value)}
          aria-label="Color theme"
        >
          <option value="system">System</option>
          <option value="light">Light</option>
          <option value="dark">Dark</option>
        </select>
      </label>
    </header>
  );
}

interface WorkspaceEditorProps {
  readonly createBoardLease: (key: string, onMode: (mode: BoardLeaseMode) => void) => BoardLease;
  readonly registerUpdateFlush: RegisterUpdateFlush | undefined;
  readonly state: Extract<ApplicationState, { kind: "workspace" }>;
  readonly theme: "light" | "dark";
  readonly onThemeChange: (theme: string) => void;
  readonly onRefresh: (
    connection: ConnectedWorkspace,
    workspaceId: string,
    session: BoardSession,
  ) => Promise<WorkspaceRefreshResult>;
  readonly onLoadPath: (path: readonly string[] | null) => Promise<void>;
  readonly onOpenBoard: (board: ScannedBoard) => Promise<void>;
  readonly onNewBoard: () => void;
  readonly onCreateFolder: (name: string) => Promise<void>;
  readonly onEmptyTrash: () => Promise<void>;
  readonly onManageBoard: (board: ScannedBoard, request: BoardManagementRequest) => Promise<void>;
  readonly onMoveItem: (
    item: WorkspaceMoveItem,
    destinationFolderPath: readonly string[],
  ) => Promise<void>;
  readonly onRestoreBoard: (board: TrashedBoard) => Promise<void>;
  readonly onTogglePin: (board: ScannedBoard) => Promise<void>;
  readonly operationProgress: DirectoryOperationProgress | null;
  readonly onCancelOperation: () => void;
  readonly onSwitchWorkspace: () => void;
}

interface ManagedBoardState {
  readonly board: ScannedBoard;
  readonly initialMode?: BoardManagementMode;
}

/** Composes the active editor, minimal navigator, and durable autosave status. */
function WorkspaceEditor({
  createBoardLease,
  registerUpdateFlush,
  state,
  theme,
  onThemeChange,
  onRefresh,
  onLoadPath,
  onOpenBoard,
  onNewBoard,
  onCreateFolder,
  onEmptyTrash,
  onManageBoard,
  onMoveItem,
  onRestoreBoard,
  onTogglePin,
  operationProgress,
  onCancelOperation,
  onSwitchWorkspace,
}: WorkspaceEditorProps) {
  const [status, setStatus] = useState<SaveStatus>({ kind: "idle" });
  const [commands, setCommands] = useState<EditorCommands | null>(null);
  const [editorUiState, setEditorUiState] = useState<EditorUiState>({
    hasElements: false,
    hasSelection: false,
    gridEnabled: false,
  });
  const [openDialog, setOpenDialog] = useState<"export" | "help" | "save_failure" | null>(null);
  const [shellDialog, setShellDialog] = useState<"create_folder" | "trash" | null>(null);
  const [managedBoard, setManagedBoard] = useState<ManagedBoardState | null>(null);
  const [managedFolder, setManagedFolder] = useState<ScannedFolder | null>(null);
  const [navigatorCollapsed, setNavigatorCollapsed] = useState(false);
  const [editorMessage, setEditorMessage] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [conflict, setConflict] = useState<BoardConflictKind | null>(null);
  const [leaseMode, setLeaseMode] = useState<BoardLeaseMode>("checking");
  const [permissionLost, setPermissionLost] = useState(false);
  const [recoveryCandidate, setRecoveryCandidate] = useState(state.session.pendingRecovery);
  const importInputRef = useRef<HTMLInputElement>(null);
  const leaseCoordinatorRef = useRef<BoardLease | null>(null);
  const latestSnapshotRef = useRef<EditorSnapshot | null>(null);
  const latestPreviewRef = useRef<PreviewProducer | null>(null);
  const session = state.session;
  const editorReadOnly = leaseMode !== "writer" || permissionLost || recoveryCandidate !== null;

  /** Avoids editor feedback renders when the visible save state is unchanged. */
  const handleStatus = useCallback((nextStatus: SaveStatus): void => {
    setStatus((currentStatus) => {
      if (currentStatus.kind !== nextStatus.kind) {
        return nextStatus;
      }
      if (nextStatus.kind === "saving" || nextStatus.kind === "idle") {
        return currentStatus;
      }
      if (
        currentStatus.kind === "failed" &&
        nextStatus.kind === "failed" &&
        currentStatus.message === nextStatus.message
      ) {
        return currentStatus;
      }
      return nextStatus;
    });
  }, []);

  /** Retains the current Shotboard-owned editor command surface. */
  const handleCommands = useCallback((nextCommands: EditorCommands | null): void => {
    setCommands(nextCommands);
  }, []);

  /** Mirrors only meaningful selection and grid state into application chrome. */
  const handleEditorUiState = useCallback((nextState: EditorUiState): void => {
    setEditorUiState((currentState) =>
      currentState.hasElements === nextState.hasElements &&
      currentState.hasSelection === nextState.hasSelection &&
      currentState.gridEnabled === nextState.gridEnabled
        ? currentState
        : nextState,
    );
  }, []);

  /** Surfaces image and export failures without losing the editable scene. */
  const handleEditorError = useCallback((message: string): void => {
    setEditorMessage(message);
    setActionNotice(null);
  }, []);

  /** Refreshes derived state and converts external divergence into explicit editor modes. */
  const handleRefresh = useCallback(async (): Promise<WorkspaceRefreshResult> => {
    const result = await onRefresh(state.connection, state.workspaceId, session);
    if (result === "permission_lost") {
      setPermissionLost(true);
    } else if (result !== "ok") {
      setConflict(result);
    }
    return result;
  }, [onRefresh, session, state.connection, state.workspaceId]);

  const controller = useMemo(
    () =>
      new AutosaveController(
        {
          stageRecovery: (snapshot) => session.stageRecovery(snapshot),
          save: async (snapshot, previewProducer) => {
            try {
              await session.save(snapshot, previewProducer);
            } catch (error) {
              if (error instanceof BoardConflictError) {
                setConflict("changed");
              } else if (isPermissionError(error)) {
                setPermissionLost(true);
              }
              throw error;
            }
          },
        },
        handleStatus,
        () => void handleRefresh(),
      ),
    [handleRefresh, handleStatus, session],
  );
  const controllerRef = useRef(controller);
  controllerRef.current = controller;
  const boardLeaseKey = session.boardId === null ? null : `${state.workspaceId}:${session.boardId}`;

  useEffect(() => {
    if (registerUpdateFlush === undefined) {
      return undefined;
    }
    return registerUpdateFlush(() => controller.flush());
  }, [controller, registerUpdateFlush]);

  /** Retains current editor data while ignoring canonical initialization callbacks. */
  const handleSnapshot = useCallback(
    (snapshot: EditorSnapshot, previewProducer: PreviewProducer, origin: EditorSnapshotOrigin) => {
      latestSnapshotRef.current = snapshot;
      latestPreviewRef.current = previewProducer;
      if (origin === "initial" || editorReadOnly || session.matchesCanonical(snapshot)) {
        return;
      }
      if (session.boardPath === null && snapshot.elements.length === 0) {
        return;
      }
      controller.schedule(snapshot, previewProducer);
    },
    [controller, editorReadOnly, session],
  );

  /** Preserves the adapter callback identity while the active board session is unchanged. */
  const handleAsset = useCallback((file: File) => session.registerAsset(file), [session]);

  useEffect(() => {
    setStatus({ kind: "idle" });
    setConflict(null);
    setPermissionLost(false);
    setRecoveryCandidate(session.pendingRecovery);
    latestSnapshotRef.current = null;
    latestPreviewRef.current = null;
  }, [session]);

  useEffect(() => {
    if (boardLeaseKey === null) {
      setLeaseMode("writer");
      leaseCoordinatorRef.current = null;
      return;
    }

    setLeaseMode("checking");
    const coordinator = createBoardLease(boardLeaseKey, setLeaseMode);
    leaseCoordinatorRef.current = coordinator;
    coordinator.setRelinquishHandler(async () => {
      if (!(await controllerRef.current.flush())) {
        throw new Error("The current writer could not flush its pending save.");
      }
    });
    void coordinator.start().catch((error: unknown) => {
      setLeaseMode("read_only");
      setEditorMessage(
        error instanceof Error
          ? `Writer coordination failed: ${error.message}`
          : "Writer coordination is unavailable in this browser.",
      );
    });
    return () => {
      coordinator.dispose();
      if (leaseCoordinatorRef.current === coordinator) {
        leaseCoordinatorRef.current = null;
      }
    };
  }, [boardLeaseKey, createBoardLease]);

  /** Requests an orderly writer handoff from the tab holding this board's lease. */
  const takeOverWriter = useCallback(async (): Promise<void> => {
    const coordinator = leaseCoordinatorRef.current;
    if (coordinator === null) {
      return;
    }
    setEditorMessage(null);
    if (!(await coordinator.takeOver())) {
      setEditorMessage(
        "Takeover was denied because the current writer could not release safely. Try again after it finishes saving.",
      );
    }
  }, []);

  /** Re-requests active workspace permission only from this explicit user gesture. */
  const reconnectPermission = useCallback(async (): Promise<void> => {
    try {
      const permission = await state.connection.gateway.requestPermission();
      if (permission !== "granted") {
        setEditorMessage("Permission remains unavailable. No workspace files were changed.");
        return;
      }
      setPermissionLost(false);
      setEditorMessage(null);
      await handleRefresh();
    } catch {
      setEditorMessage(permissionRequestError());
    }
  }, [handleRefresh, state.connection.gateway]);

  /** Reloads the disk document only after the user accepts losing in-memory divergence. */
  const reloadConflict = useCallback(async (): Promise<void> => {
    controller.discardPending();
    await onLoadPath(conflict === "changed" ? session.boardPath : null);
  }, [conflict, controller, onLoadPath, session.boardPath]);

  /** Writes the retained in-memory snapshot to a collision-safe sibling board. */
  const saveConflictAsCopy = useCallback(async (): Promise<void> => {
    const snapshot = latestSnapshotRef.current;
    if (snapshot === null) {
      throw new Error("The local editor has not finished loading its retained state.");
    }
    const copy = await session.saveCurrentAsCopy(snapshot, latestPreviewRef.current ?? undefined);
    controller.discardPending();
    await onLoadPath(copy.boardPath);
  }, [controller, onLoadPath, session]);

  /** Restores the validated newer recovery document after revision revalidation. */
  const restoreRecovery = useCallback(async (): Promise<void> => {
    const restored = await session.restoreRecovery();
    controller.discardPending();
    await onLoadPath(restored.boardPath);
  }, [controller, onLoadPath, session]);

  /** Keeps canonical disk state and removes only its browser-local recovery candidate. */
  const discardRecovery = useCallback(async (): Promise<void> => {
    await session.discardRecovery();
    controller.discardPending();
    setRecoveryCandidate(null);
  }, [controller, session]);

  /** Writes the validated recovery candidate into a collision-safe sibling board. */
  const saveRecoveryAsCopy = useCallback(async (): Promise<void> => {
    const copy = await session.saveRecoveryAsCopy();
    controller.discardPending();
    await onLoadPath(copy.boardPath);
  }, [controller, onLoadPath, session]);

  /** Downloads the complete validated recovery JSON without changing disk state. */
  const downloadRecovery = useCallback((): void => {
    if (recoveryCandidate === null) {
      return;
    }
    downloadBlob(
      new Blob([recoveryCandidate.record.documentJson], { type: "application/json" }),
      `${session.boardPath?.at(-1) ?? "shotboard"}-recovery.json`,
    );
  }, [recoveryCandidate, session.boardPath]);

  /** Downloads the newest in-memory candidate as a complete validated recovery document. */
  const downloadFailedSaveRecovery = useCallback(async (): Promise<void> => {
    const snapshot = latestSnapshotRef.current;
    if (snapshot === null) {
      throw new Error("The local editor has not produced a recovery snapshot yet.");
    }
    downloadBlob(
      new Blob([session.createRecoveryJson(snapshot)], { type: "application/json" }),
      `${session.boardPath?.at(-1) ?? "shotboard"}-recovery.json`,
    );
  }, [session]);

  /** Writes one failed in-memory save candidate to a collision-safe sibling board. */
  const saveFailedAsCopy = useCallback(async (): Promise<void> => {
    const snapshot = latestSnapshotRef.current;
    if (snapshot === null) {
      throw new Error("The local editor has not produced a recovery snapshot yet.");
    }
    const copy = await session.saveCurrentAsCopy(snapshot, latestPreviewRef.current ?? undefined);
    controller.discardPending();
    await onLoadPath(copy.boardPath);
  }, [controller, onLoadPath, session]);

  /** Copies the current selection, or all content when nothing is selected, as PNG. */
  const copyAsPng = useCallback(async (): Promise<void> => {
    if (commands === null || !editorUiState.hasElements) {
      return;
    }
    setEditorMessage(null);
    try {
      await commands.copyAsPng(editorUiState.hasSelection ? "selection" : "all");
      setActionNotice("Flattened PNG copied.");
    } catch (error) {
      handleEditorError(
        error instanceof Error ? error.message : "The flattened PNG could not be copied.",
      );
    }
  }, [commands, editorUiState.hasElements, editorUiState.hasSelection, handleEditorError]);

  /** Imports files selected through the explicit Shotboard file picker. */
  const importSelectedImages = useCallback(
    async (files: FileList | null): Promise<void> => {
      if (commands === null || files === null || files.length === 0) {
        return;
      }
      setEditorMessage(null);
      try {
        await commands.importImages([...files]);
        setActionNotice((currentNotice) =>
          currentNotice?.startsWith("Large image")
            ? currentNotice
            : files.length === 1
              ? "Image imported."
              : `${files.length} images imported.`,
        );
      } catch (error) {
        handleEditorError(
          error instanceof Error ? error.message : "The local image import failed.",
        );
      } finally {
        if (importInputRef.current !== null) {
          importInputRef.current.value = "";
        }
      }
    },
    [commands, handleEditorError],
  );

  useEffect(() => {
    /** Handles application shortcuts without stealing keystrokes from text controls. */
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isTextEntry(event.target)) {
        return;
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "s") {
        event.preventDefault();
        void controller.flush();
      } else if (
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey &&
        event.key.toLowerCase() === "c"
      ) {
        event.preventDefault();
        void copyAsPng();
      } else if (
        (event.metaKey || event.ctrlKey) &&
        event.shiftKey &&
        event.key.toLowerCase() === "e"
      ) {
        event.preventDefault();
        if (commands !== null && editorUiState.hasElements) {
          setOpenDialog("export");
        }
      } else if (event.key === "?") {
        event.preventDefault();
        setOpenDialog("help");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [commands, controller, copyAsPng, editorUiState.hasElements]);

  useEffect(() => {
    /** Flushes on blur and reflects external Finder changes when focus returns. */
    const handleBlur = () => void controller.flush();
    const handleFocus = () => void handleRefresh();
    window.addEventListener("blur", handleBlur);
    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("focus", handleFocus);
      controller.dispose();
    };
  }, [controller, handleRefresh]);

  /** Flushes the current board before switching editor sessions. */
  const handleOpenBoard = useCallback(
    async (board: ScannedBoard) => {
      if (!(await controller.flush())) {
        return;
      }
      await onOpenBoard(board);
    },
    [controller, onOpenBoard],
  );

  /** Flushes the current board before creating a fresh draft. */
  const handleNewBoard = useCallback(async () => {
    if (await controller.flush()) {
      onNewBoard();
    }
  }, [controller, onNewBoard]);

  /** Flushes the current board before leaving its workspace shell. */
  const handleSwitchWorkspace = useCallback(async () => {
    if (await controller.flush()) {
      onSwitchWorkspace();
    }
  }, [controller, onSwitchWorkspace]);

  /** Flushes the current board before a journaled board operation begins. */
  const handleManageBoard = useCallback(
    async (request: BoardManagementRequest): Promise<void> => {
      if (managedBoard === null) {
        return;
      }
      if (!(await controller.flush())) {
        return;
      }
      await onManageBoard(managedBoard.board, request);
    },
    [controller, managedBoard, onManageBoard],
  );

  /** Flushes active edits before moving any board or folder through the shared journal. */
  const handleMoveItem = useCallback(
    async (item: WorkspaceMoveItem, destinationFolderPath: readonly string[]): Promise<void> => {
      if (!(await controller.flush())) {
        throw new Error("The current board must save successfully before moving workspace files.");
      }
      await onMoveItem(item, destinationFolderPath);
    },
    [controller, onMoveItem],
  );

  /** Surfaces portable pin failures without hiding the active editor. */
  const handleTogglePin = useCallback(
    async (board: ScannedBoard): Promise<void> => {
      try {
        await onTogglePin(board);
      } catch (error) {
        handleEditorError(
          error instanceof Error ? error.message : "The board pin could not be updated.",
        );
      }
    },
    [handleEditorError, onTogglePin],
  );

  const boardName = session.boardPath?.at(-1) ?? "New board";

  return (
    <>
      <main className={`workspace-frame${navigatorCollapsed ? " workspace-frame--collapsed" : ""}`}>
        <WorkspaceNavigator
          activePath={session.boardPath}
          collapsed={navigatorCollapsed}
          gateway={state.connection.gateway}
          pinnedPaths={state.manifest.pinnedBoardPaths}
          recentPaths={state.recentBoardPaths}
          scan={state.scan}
          trashCount={state.trash.length}
          workspaceName={state.connection.gateway.name}
          onCreateFolder={() => setShellDialog("create_folder")}
          onManageBoard={(board) => setManagedBoard({ board })}
          onManageFolder={setManagedFolder}
          onMoveItem={handleMoveItem}
          onNewBoard={() => void handleNewBoard()}
          onOpenBoard={(board) => void handleOpenBoard(board)}
          onRenameBoard={(board) => setManagedBoard({ board, initialMode: "rename" })}
          onRefresh={() => void handleRefresh()}
          onShowTrash={() => setShellDialog("trash")}
          onSwitchWorkspace={() => void handleSwitchWorkspace()}
          onToggleCollapsed={() => setNavigatorCollapsed((current) => !current)}
          onTogglePin={(board) => void handleTogglePin(board)}
        />

        <section className="board-shell" aria-label={`Editing ${boardName}`}>
          <header className="board-header">
            <div>
              <p>{state.connection.gateway.name}</p>
              <h1>{boardName}</h1>
            </div>
            <div className="board-header__right">
              <div className="board-tools" role="toolbar" aria-label="Board actions">
                <input
                  ref={importInputRef}
                  className="visually-hidden"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,.svg"
                  multiple
                  aria-label="Choose image files"
                  onChange={(event) => void importSelectedImages(event.currentTarget.files)}
                />
                <button
                  type="button"
                  disabled={commands === null || editorReadOnly}
                  onClick={() => importInputRef.current?.click()}
                >
                  <Upload aria-hidden="true" />
                  Import
                </button>
                <button
                  type="button"
                  disabled={commands === null || editorReadOnly}
                  onClick={() => {
                    commands?.applyOpaqueRedaction();
                    setActionNotice("Opaque Redaction selected.");
                    setEditorMessage(null);
                  }}
                >
                  <ScanLine aria-hidden="true" />
                  Redact
                </button>
                <button
                  type="button"
                  disabled={commands === null || !editorUiState.hasElements}
                  onClick={() => void copyAsPng()}
                >
                  <ClipboardCopy aria-hidden="true" />
                  Copy PNG
                </button>
                <button
                  type="button"
                  disabled={commands === null || !editorUiState.hasElements}
                  onClick={() => setOpenDialog("export")}
                >
                  <Download aria-hidden="true" />
                  Export
                </button>
                <button
                  type="button"
                  disabled={commands === null || editorReadOnly}
                  aria-pressed={editorUiState.gridEnabled}
                  onClick={() => {
                    const enabled = !editorUiState.gridEnabled;
                    commands?.setGridEnabled(enabled);
                    setEditorUiState((current) => ({ ...current, gridEnabled: enabled }));
                  }}
                >
                  <Grid3X3 aria-hidden="true" />
                  Grid
                </button>
                <button type="button" onClick={() => setOpenDialog("help")}>
                  <HelpCircle aria-hidden="true" />
                  Help
                </button>
              </div>
              <div className="board-header__actions">
                <SaveStatusView
                  status={status}
                  onRecovery={() => setOpenDialog("save_failure")}
                  onRetry={() => void controller.retry()}
                />
                <select
                  value={theme}
                  onChange={(event) => onThemeChange(event.currentTarget.value)}
                  aria-label="Color theme"
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="system">System</option>
                </select>
              </div>
            </div>
          </header>
          {editorMessage === null ? null : (
            <div
              className={`editor-alert${permissionLost || leaseMode !== "writer" ? " editor-alert--below-banner" : ""}`}
              role="alert"
            >
              <span>{editorMessage}</span>
              <button type="button" onClick={() => setEditorMessage(null)}>
                Dismiss
              </button>
            </div>
          )}
          {actionNotice === null ? null : (
            <p className="action-notice" role="status">
              {actionNotice}
            </p>
          )}
          {operationProgress === null ? null : (
            <div className="operation-progress" role="status">
              <span>
                {operationProgress.phase.replaceAll("_", " ")} ·{" "}
                {operationProgress.completedEntries}/{operationProgress.totalEntries}
              </span>
              <button type="button" onClick={onCancelOperation}>
                Cancel safely
              </button>
            </div>
          )}
          {permissionLost ? (
            <div className="workspace-banner" role="alert">
              <span>Workspace permission expired. No files were changed.</span>
              <button type="button" onClick={() => void reconnectPermission()}>
                Request File Access
              </button>
            </div>
          ) : leaseMode === "checking" ? (
            <div className="workspace-banner" role="status">
              Checking this board’s writer lease…
            </div>
          ) : leaseMode === "read_only" ? (
            <div className="workspace-banner" role="status">
              <span>Another Shotboard tab is editing this board. This tab is read-only.</span>
              <button type="button" onClick={() => void takeOverWriter()}>
                Take Over
              </button>
              <button type="button" onClick={() => void reconnectPermission()}>
                Request File Access
              </button>
            </div>
          ) : null}
          <div className="canvas-region">
            <Suspense fallback={<p className="editor-loading">Loading local editor…</p>}>
              <ExcalidrawAdapter
                key={session.editorSessionKey}
                sceneJson={session.sceneJson}
                theme={theme}
                readOnly={editorReadOnly}
                onAsset={handleAsset}
                onCommands={handleCommands}
                onError={handleEditorError}
                onSnapshot={handleSnapshot}
                onUiState={handleEditorUiState}
                onWarning={setActionNotice}
              />
            </Suspense>
            {session.boardPath === null ? (
              <div className="canvas-empty" aria-hidden="true">
                <ImagePlus />
                <strong>Paste, drop, or import an image</strong>
                <span>⌘V pastes a screenshot · Image tool shortcut: 9</span>
              </div>
            ) : null}
          </div>
          <div className="desktop-required" role="status">
            Shotboard editing requires a desktop viewport of at least 1024 × 700.
          </div>
        </section>
      </main>
      {openDialog === "export" && commands !== null ? (
        <ExportDialog
          boardName={boardName}
          commands={commands}
          hasSelection={editorUiState.hasSelection}
          onClose={() => setOpenDialog(null)}
        />
      ) : null}
      {openDialog === "help" ? <HelpDialog onClose={() => setOpenDialog(null)} /> : null}
      {openDialog === "save_failure" && status.kind === "failed" ? (
        <SaveFailureDialog
          canSaveCopy={!permissionLost && session.boardPath !== null}
          message={status.message}
          onClose={() => setOpenDialog(null)}
          onDownload={downloadFailedSaveRecovery}
          onRetry={() => controller.retry()}
          onSaveCopy={saveFailedAsCopy}
        />
      ) : null}
      {managedBoard === null ? null : (
        <BoardManagementDialog
          board={managedBoard.board}
          folders={state.scan.folders}
          initialMode={managedBoard.initialMode}
          onClose={() => setManagedBoard(null)}
          onSubmit={handleManageBoard}
        />
      )}
      {managedFolder === null ? null : (
        <FolderMoveDialog
          folder={managedFolder}
          folders={state.scan.folders}
          onClose={() => setManagedFolder(null)}
          onMove={handleMoveItem}
        />
      )}
      {shellDialog === "create_folder" ? (
        <CreateFolderDialog onClose={() => setShellDialog(null)} onCreate={onCreateFolder} />
      ) : null}
      {shellDialog === "trash" ? (
        <TrashDialog
          boards={state.trash}
          onClose={() => setShellDialog(null)}
          onEmpty={onEmptyTrash}
          onRestore={onRestoreBoard}
        />
      ) : null}
      {conflict === null ? null : (
        <ConflictDialog
          kind={conflict}
          onCancel={() => setConflict(null)}
          onReload={reloadConflict}
          onSaveCopy={saveConflictAsCopy}
        />
      )}
      {recoveryCandidate !== null && leaseMode === "writer" && !permissionLost ? (
        <RecoveryCopyDialog
          candidate={recoveryCandidate}
          canonicalUpdatedAt={session.canonicalUpdatedAt ?? recoveryCandidate.document.createdAt}
          onDiscard={discardRecovery}
          onDownload={downloadRecovery}
          onRestore={restoreRecovery}
          onSaveCopy={saveRecoveryAsCopy}
        />
      ) : null}
    </>
  );
}

/** Reads a stored last-board preference without trusting path text. */
function readLastBoardPath(): readonly string[] | null {
  const stored = window.localStorage.getItem(LAST_BOARD_KEY);
  if (stored === null) {
    return null;
  }
  try {
    return parseStoredPath(stored);
  } catch {
    window.localStorage.removeItem(LAST_BOARD_KEY);
    return null;
  }
}

/** Creates missing organizational parents without replacing an existing workspace entry. */
async function ensureFolderPath(
  connection: ConnectedWorkspace,
  path: readonly string[],
): Promise<void> {
  const current: string[] = [];
  for (const segment of path) {
    current.push(segment);
    if (!(await connection.gateway.fileExists(current))) {
      await connection.gateway.createDirectory(current);
      continue;
    }
    try {
      await connection.gateway.listDirectory(current);
    } catch {
      throw new Error(`Restore parent is not a folder: ${formatStoredPath(current)}.`);
    }
  }
}

/** Reads the effective document theme already maintained by ThemeProvider. */
function resolvedDocumentTheme(): "light" | "dark" {
  return document.documentElement.dataset.theme === "dark" ? "dark" : "light";
}

/** Prevents application shortcuts from firing during text and form entry. */
function isTextEntry(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

/** Identifies browser filesystem permission failures without matching unrelated errors. */
function isPermissionError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "NotAllowedError";
}

/** Converts browser permission failures into one safe, actionable message. */
function permissionRequestError(): string {
  return "The browser did not grant file access. No workspace files were changed. Try Request File Access again or switch workspaces.";
}
